// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Star, 
  Video, 
  Package, 
  Calendar,
  User,
  Settings,
  MousePointer,
  Loader2,
  AlertCircle,
  DollarSign,
  CreditCard
} from 'lucide-react';
import { toast } from 'sonner';
import ProfileManagementModal from '@/components/vendor/ProfileManagementModal';
import PackageManagementModal from '@/components/vendor-video/PackageManagementModal';
import EventSelectionModal from '@/components/vendor-video/EventSelectionModal';
import VendorVideoMessaging from '@/components/video/VendorVideoMessaging';

interface VideoVendorProfile {
  id: string;
  user_id: string;
  business_name?: string;
  rating_average?: number;
  review_count?: number;
  description?: string;
  created_at: string;
  updated_at: string;
}

interface DashboardStats {
  rating: number;
  portfolioVideos: number;
  packages: number;
  events: number;
  totalEarnings: number;
  pendingPayouts: number;
}

const VendorVideoDashboard: React.FC = () => {
  const { user, profile } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [vendorProfile, setVendorProfile] = useState<VideoVendorProfile | null>(null);
  const [stats, setStats] = useState<DashboardStats>({
    rating: 0,
    portfolioVideos: 0,
    packages: 0,
    events: 0,
    totalEarnings: 0,
    pendingPayouts: 0
  });
  const [error, setError] = useState<string | null>(null);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showPackageModal, setShowPackageModal] = useState(false);
  const [showEventModal, setShowEventModal] = useState(false);
  const [pendingOrders, setPendingOrders] = useState([]);

  useEffect(() => {
    checkAccess();
  }, [profile]);

  // Fetch pending orders when vendor profile is available
  useEffect(() => {
    if (vendorProfile?.id) {
      fetchPendingOrders();
    }
  }, [vendorProfile?.id]);

  const checkAccess = async () => {
    try {
      // Check if user has vendor_video membership
      if (!profile || profile.active_membership_slug !== 'vendor_video') {
        toast.error('Access denied. You need a Video Vendor membership to access this page.');
        navigate('/');
        return;
      }

      await fetchOrCreateVendorProfile();
      await fetchDashboardStats();
    } catch (error) {
      console.error('Error checking access:', error);
      setError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const fetchOrCreateVendorProfile = async () => {
    if (!user?.id) return;

    // Try to fetch existing profile
    const { data: existingProfile, error: fetchError } = await supabase
      .from('video_vendor_profiles')
      .select('*')
      .eq('user_id', user.id)
      .maybeSingle();

    if (fetchError) {
      console.error('Error fetching vendor profile:', fetchError);
      throw fetchError;
    }

    if (existingProfile) {
      setVendorProfile(existingProfile);
    } else {
      // Create new profile
      const { data: newProfile, error: createError } = await supabase
        .from('video_vendor_profiles')
        .insert({
          user_id: user.id,
          business_name: profile?.name || 'My Video Business',
          rating_average: 0,
          review_count: 0
        })
        .select()
        .maybeSingle();

      if (createError) {
        console.error('Error creating vendor profile:', createError);
        throw createError;
      }

      setVendorProfile(newProfile);
    }
  };

  const fetchDashboardStats = async () => {
    if (!user?.id) return;

    try {
      // Get vendor profile for rating and package counting
      const { data: vendorData } = await supabase
        .from('video_vendor_profiles')
        .select('id, rating_average, review_count')
        .eq('user_id', user.id)
        .maybeSingle();

      // Get user's commission rate
      const { data: profileData } = await supabase
        .from('profiles')
        .select('commission_rate')
        .eq('user_id', user.id)
        .single();

      const commissionRate = profileData?.commission_rate || 10;

      // Get packages count using vendor profile ID
      let packagesCount = 0;
      if (vendorData?.id) {
        const { count } = await supabase
          .from('video_packages')
          .select('*', { count: 'exact', head: true })
          .eq('vendor_id', vendorData.id);
        packagesCount = count || 0;
      }

      // Get events count using vendor profile ID
      let eventsCount = 0;
      if (vendorData?.id) {
        const { count } = await supabase
          .from('vendor_event_availability')
          .select('*', { count: 'exact', head: true })
          .eq('vendor_id', vendorData.id);
        eventsCount = count || 0;
      }

      // Get financial data
      let totalEarnings = 0;
      let pendingPayouts = 0;
      
      if (vendorData?.id) {
        // Get completed orders
        const { data: completedOrders } = await supabase
          .from('video_orders')
          .select('total_amount_cents')
          .eq('vendor_id', vendorData.id)
          .eq('status', 'completed');

        const totalRevenue = (completedOrders?.reduce((sum, order) => sum + (order.total_amount_cents || 0), 0) || 0) / 100;
        totalEarnings = totalRevenue * (commissionRate / 100);

        // Get payout history
        const { data: payouts } = await supabase
          .from('affiliate_payouts')
          .select('amount, status')
          .eq('affiliate_id', user.id);

        const paidAmount = payouts?.filter(p => p.status === 'completed')
          .reduce((sum, p) => sum + p.amount, 0) || 0;
        
        const pendingAmount = payouts?.filter(p => p.status === 'pending')
          .reduce((sum, p) => sum + p.amount, 0) || 0;

        pendingPayouts = Math.max(0, totalEarnings - paidAmount - pendingAmount);
      }

      setStats({
        rating: vendorData?.rating_average || 0,
        portfolioVideos: 0, // We'll implement this when portfolio is ready
        packages: packagesCount,
        events: eventsCount,
        totalEarnings,
        pendingPayouts
      });
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
    }
  };

  const fetchPendingOrders = async () => {
    if (!vendorProfile?.id) return;

    try {
      console.log('Fetching orders for vendor:', vendorProfile.id);
      const { data, error } = await supabase
        .from('video_orders')
        .select('*')
        .eq('vendor_id', vendorProfile.id)
        .in('status', ['pending', 'in_progress'])
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Supabase error:', error);
        throw error;
      }
      
      console.log('Fetched orders:', data);
      setPendingOrders(data || []);
    } catch (error) {
      console.error('Error fetching pending orders:', error);
      toast.error('Failed to load pending orders');
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto">
        <div className="flex items-center justify-center min-h-[calc(100vh-200px)]">
          <div className="flex items-center gap-2">
            <Loader2 className="h-6 w-6 animate-spin" />
            <span>Loading your dashboard...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto">
        <div className="flex items-center justify-center min-h-[calc(100vh-200px)]">
          <Card className="max-w-md">
            <CardContent className="pt-6">
              <div className="flex items-center gap-2 text-destructive mb-4">
                <AlertCircle className="h-5 w-5" />
                <span className="font-medium">Error</span>
              </div>
              <p className="text-muted-foreground mb-4">{error}</p>
              <Button onClick={() => window.location.reload()}>
                Try Again
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const statCards = [
    {
      title: 'Rating',
      value: stats.rating.toFixed(1),
      icon: Star,
      description: 'Average rating',
      color: 'text-yellow-600'
    },
    {
      title: 'Portfolio Videos',
      value: stats.portfolioVideos.toString(),
      icon: Video,
      description: 'Videos in portfolio',
      color: 'text-blue-600'
    },
    {
      title: 'Packages',
      value: stats.packages.toString(),
      icon: Package,
      description: 'Service packages',
      color: 'text-green-600'
    },
    {
      title: 'Total Earnings',
      value: `$${stats.totalEarnings.toFixed(2)}`,
      icon: DollarSign,
      description: 'Total commission earned',
      color: 'text-green-600'
    },
    {
      title: 'Available Payout',
      value: `$${stats.pendingPayouts.toFixed(2)}`,
      icon: CreditCard,
      description: 'Ready for withdrawal',
      color: 'text-blue-600'
    },
    {
      title: 'Events',
      value: stats.events.toString(),
      icon: Calendar,
      description: 'Available events',
      color: 'text-purple-600'
    }
  ];

  const actionCards = [
    {
      title: 'Profile Setup',
      description: 'Complete your business profile, add your bio, service areas, and contact information.',
      icon: User,
      buttonText: 'Manage Profile'
    },
    {
      title: 'Package Management',
      description: 'Create and manage your video service packages, pricing, and availability.',
      icon: Package,
      buttonText: 'Manage Packages'
    },
    {
      title: 'Event Selection',
      description: 'Browse and select events where you want to offer your video services.',
      icon: MousePointer,
      buttonText: 'Select Events'
    }
  ];

  return (
    <div className="container mx-auto">
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold tracking-tight">Vendor Video Dashboard</h1>
              <p className="text-muted-foreground">
                Manage your video services, packages, and bookings
              </p>
            </div>
            <Badge variant="secondary" className="bg-blue-50 text-blue-700">
              Video Vendor
            </Badge>
          </div>
        </div>

        {/* Welcome Message */}
        {vendorProfile && (
          <Card className="bg-gradient-to-r from-blue-50 to-purple-50 border-blue-200">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <Video className="h-8 w-8 text-blue-600" />
                <div>
                  <h3 className="font-semibold text-blue-900">
                    Welcome, {vendorProfile.business_name}!
                  </h3>
                  <p className="text-blue-700 text-sm">
                    Ready to create amazing video content for athletic events
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Stats Cards */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {statCards.map((stat) => {
            const Icon = stat.icon;
            return (
              <Card key={stat.title}>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">
                    {stat.title}
                  </CardTitle>
                  <Icon className={`h-4 w-4 ${stat.color}`} />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{stat.value}</div>
                  <p className="text-xs text-muted-foreground">
                    {stat.description}
                  </p>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Action Cards */}
        <div className="grid gap-6 md:grid-cols-3">
          {actionCards.map((action) => {
            const Icon = action.icon;
            return (
              <Card key={action.title} className="relative">
                <CardHeader>
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-primary/10 rounded-lg">
                      <Icon className="h-5 w-5 text-primary" />
                    </div>
                    <CardTitle className="text-lg">{action.title}</CardTitle>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <CardDescription className="text-sm leading-relaxed">
                    {action.description}
                  </CardDescription>
                   <Button 
                     className="w-full" 
                     variant="outline"
                     onClick={() => {
                       if (action.title === 'Profile Setup') {
                         setShowProfileModal(true);
                       } else if (action.title === 'Package Management') {
                         setShowPackageModal(true);
                       } else if (action.title === 'Event Selection') {
                         setShowEventModal(true);
                       }
                     }}
                   >
                     {action.buttonText}
                   </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Quick Actions */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Settings className="h-5 w-5" />
              Quick Actions
            </CardTitle>
            <CardDescription>
              Common tasks to get you started
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-3 md:grid-cols-3">
              <Button 
                variant="outline" 
                className="justify-start"
                onClick={() => setShowPackageModal(true)}
              >
                <Package className="h-4 w-4 mr-2" />
                Create Package
              </Button>
              <Button variant="outline" className="justify-start">
                <Calendar className="h-4 w-4 mr-2" />
                View Calendar
              </Button>
              <Button 
                variant="outline" 
                className="justify-start"
                onClick={() => navigate('/vendor-video-dashboard/finance')}
              >
                <DollarSign className="h-4 w-4 mr-2" />
                View Finance
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Customer Messages Section */}
        {vendorProfile && (
          <VendorVideoMessaging vendorId={vendorProfile.id} />
        )}

        {/* Modals */}
        <ProfileManagementModal
          open={showProfileModal}
          onOpenChange={setShowProfileModal}
          vendorProfile={vendorProfile}
          onProfileUpdate={fetchDashboardStats}
        />
        
        {vendorProfile && (
          <>
            <PackageManagementModal
              open={showPackageModal}
              onOpenChange={setShowPackageModal}
              vendorId={vendorProfile.id}
              onPackageUpdate={fetchDashboardStats}
            />
            
            <EventSelectionModal
              open={showEventModal}
              onOpenChange={setShowEventModal}
              vendorId={vendorProfile.id}
              onEventUpdate={fetchDashboardStats}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default VendorVideoDashboard;